/**
 * Tauri external-URL bridge
 *
 * Two problems to solve in Tauri (WKWebView / WebView2):
 *
 * 1. `window.open(url, '_blank')` — JS-triggered popups (NotificationBar, attachments…).
 *    Override routes HTTP(S) URLs to the system browser via plugin-opener.
 *    Non-HTTP URLs (in-app routes, about:blank) fall through to the original handler.
 *
 * 2. `<a href="https://…" target="_blank">` — native link clicks bypass window.open
 *    entirely and go through the WebKit/WebView2 navigation delegate, which Tauri
 *    silently drops. A document-level click listener catches them first.
 *
 * Keycloak auth uses a redirect in the main window (`login_Keycloak` / `?code=`),
 * so no OAuth popup or window.opener bridge is required here.
 *
 * Call once at startup (before React renders). No-op outside Tauri.
 * @see https://v2.tauri.app/reference/javascript/opener/
 */

import { isTauri } from "@/lib/utils/tauriUtils";
import { openUrl } from "@tauri-apps/plugin-opener";

const isExternalUrl = (url: string) => /^(https?:\/\/|mailto:)/i.test(url);

const safeOpenUrl = (url: string): void => {
    openUrl(url).catch((err) =>
        console.error("[tauriLinkBridge] openUrl failed:", err),
    );
};

// Intercept window.open() calls made by JS code.
const overrideWindowOpen = (): void => {
    const originalOpen = window.open.bind(window);

    window.open = (
        url?: string | URL,
        target?: string,
        features?: string,
    ): WindowProxy | null => {
        const urlStr = url != null ? String(url) : undefined;
        const isPopup = !target || target === "_blank";

        if (isPopup && urlStr && isExternalUrl(urlStr)) {
            safeOpenUrl(urlStr);
            return null;
        }

        return originalOpen(url, target, features);
    };
};

// Intercept native <a href> clicks that bypass window.open in WKWebView / WebView2.
const interceptAnchorClicks = (): (() => void) => {
    const handleClick = (event: MouseEvent) => {
        const anchor = (event.target as Element).closest("a");
        if (!anchor) return;

        const href = anchor.getAttribute("href") ?? "";
        if (!isExternalUrl(href)) return;

        event.preventDefault();
        safeOpenUrl(href);
    };

    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
};

export const setupTauriLinkBridge = (): (() => void) => {
    if (!isTauri()) return () => {};

    overrideWindowOpen();
    return interceptAnchorClicks();
};
